import React, { useEffect, useState } from "react";
import { BsPersonCircle, BsShieldCheck, BsChevronDown, BsChevronUp } from "react-icons/bs";
import { FiLogIn, FiCheckSquare, FiVideo, FiAlertTriangle } from "react-icons/fi";
import { ImSpinner2 } from "react-icons/im";

interface MissedDetail {
  patient: string;
  from: string;
  to: string;
  gapMinutes: number;
}

interface AuditUser {
  user: { sub: string; fullname: string; username: string; role: string };
  logins: string[];
  checks: { timestamp: string; patientname: string; systemname: string; note: string | null; username: string }[];
  liveViews: { started_at: string; ended_at: string | null; duration_seconds: number | null; patientname: string }[];
  missedChecks: number;
  missedDetails: MissedDetail[];
  totalLiveDuration: number;
}

const fmt = (ts: string | number) =>
  new Intl.DateTimeFormat("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  }).format(new Date(ts));

const fmtDuration = (seconds: number) => {
  if (seconds < 60) return `${seconds}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const toInputDate = (d: Date) => d.toISOString().slice(0, 10);

export default function AuditLog() {
  const [data, setData] = useState<AuditUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const now = new Date();
  const [startDate, setStartDate] = useState(toInputDate(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)));
  const [endDate, setEndDate] = useState(toInputDate(now));

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `/api/v1/auditlogs?start=${startDate}&end=${endDate}T23:59:59`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to load audit logs");
      const json = await res.json();
      setData(json.auditData);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const toggleExpanded = (sub: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(sub) ? next.delete(sub) : next.add(sub);
      return next;
    });
  };

  const totalChecks = data.reduce((a, u) => a + u.checks.length, 0);
  const totalMissed = data.reduce((a, u) => a + u.missedChecks, 0);
  const activeUsers = data.filter(u => u.checks.length > 0 || u.liveViews.length > 0).length;

  return (
    <div style={{ padding: "20px 24px", paddingBottom: "80px", maxWidth: "900px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "20px" }}>
        <h1 style={{ fontSize: "20px", fontWeight: 700, color: "#0f172a", margin: 0 }}>Audit Logs</h1>
        <p style={{ fontSize: "13px", color: "#64748b", margin: "4px 0 0" }}>Staff activity and compliance monitoring</p>
      </div>

      {/* Date filter */}
      <div style={{ display: "flex", gap: "10px", alignItems: "flex-end", marginBottom: "20px", flexWrap: "wrap" }}>
        <div>
          <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>From</label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            style={{ padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", color: "#0f172a" }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>To</label>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            style={{ padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", color: "#0f172a" }}
          />
        </div>
        <button
          onClick={fetchData}
          style={{ padding: "8px 18px", background: "#2563eb", color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
        >
          Apply
        </button>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
          <ImSpinner2 style={{ animation: "spin 0.6s linear infinite", fontSize: "24px" }} />
        </div>
      )}

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "14px 18px", color: "#dc2626", fontSize: "13px" }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Summary cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", marginBottom: "20px" }}>
            {[
              { label: "Active staff", value: activeUsers, icon: <BsPersonCircle size={18} />, color: "#2563eb", bg: "#eff6ff" },
              { label: "Total checks", value: totalChecks, icon: <FiCheckSquare size={18} />, color: "#059669", bg: "#f0fdf4" },
              { label: "Missed windows", value: totalMissed, icon: <FiAlertTriangle size={18} />, color: totalMissed > 0 ? "#dc2626" : "#059669", bg: totalMissed > 0 ? "#fef2f2" : "#f0fdf4" },
            ].map(card => (
              <div key={card.label} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: card.color, marginBottom: "8px" }}>
                  <div style={{ background: card.bg, padding: "6px", borderRadius: "8px" }}>{card.icon}</div>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280" }}>{card.label}</span>
                </div>
                <div style={{ fontSize: "28px", fontWeight: 700, color: card.color }}>{card.value}</div>
              </div>
            ))}
          </div>

          {/* Per-user cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {data.map(entry => {
              const isOpen = expanded.has(entry.user.sub);
              const hasMissed = entry.missedChecks > 0;
              return (
                <div key={entry.user.sub} style={{ background: "#fff", border: `1px solid ${hasMissed ? "#fca5a5" : "#e5e7eb"}`, borderRadius: "12px", overflow: "hidden" }}>
                  {/* User header row */}
                  <div
                    onClick={() => toggleExpanded(entry.user.sub)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", cursor: "pointer", userSelect: "none" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div style={{ width: "36px", height: "36px", background: "#f1f5f9", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <BsPersonCircle size={20} color="#64748b" />
                      </div>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>{entry.user.fullname}</span>
                          {(entry.user.role === "admin" || entry.user.role === "superadmin") && (
                            <span style={{ background: "#eff6ff", color: "#2563eb", fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "20px" }}>ADMIN</span>
                          )}
                        </div>
                        <span style={{ fontSize: "12px", color: "#94a3b8" }}>{entry.user.username}</span>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                      {/* Stats pills */}
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <Pill icon={<FiLogIn size={11} />} label={`${entry.logins.length} logins`} color="#2563eb" bg="#eff6ff" />
                        <Pill icon={<FiCheckSquare size={11} />} label={`${entry.checks.length} checks`} color="#059669" bg="#f0fdf4" />
                        <Pill icon={<FiVideo size={11} />} label={`${entry.liveViews.length} live views`} color="#7c3aed" bg="#faf5ff" />
                        {hasMissed && <Pill icon={<FiAlertTriangle size={11} />} label={`${entry.missedChecks} missed`} color="#dc2626" bg="#fef2f2" />}
                      </div>
                      {isOpen ? <BsChevronUp size={14} color="#94a3b8" /> : <BsChevronDown size={14} color="#94a3b8" />}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isOpen && (
                    <div style={{ borderTop: "1px solid #f1f5f9", padding: "16px 18px", display: "flex", flexDirection: "column", gap: "16px" }}>
                      {/* Logins */}
                      {entry.logins.length > 0 && (
                        <Section title="Login times" color="#2563eb">
                          {entry.logins.map((l, i) => (
                            <Row key={i} left={fmt(l)} />
                          ))}
                        </Section>
                      )}

                      {/* Checks */}
                      {entry.checks.length > 0 && (
                        <Section title="Checks performed" color="#059669">
                          {entry.checks.map((c, i) => (
                            <Row key={i} left={c.patientname} right={fmt(c.timestamp)} sub={c.note || "No notes"} />
                          ))}
                        </Section>
                      )}

                      {/* Live views */}
                      {entry.liveViews.length > 0 && (
                        <Section title={`Live views · ${fmtDuration(entry.totalLiveDuration)} total`} color="#7c3aed">
                          {entry.liveViews.map((l, i) => (
                            <Row
                              key={i}
                              left={l.patientname}
                              right={fmt(l.started_at)}
                              sub={l.duration_seconds ? fmtDuration(l.duration_seconds) : "interrupted"}
                            />
                          ))}
                        </Section>
                      )}

                      {/* Missed check windows */}
                      {entry.missedDetails.length > 0 && (
                        <Section title="Missed check windows" color="#dc2626">
                          {entry.missedDetails.map((m, i) => (
                            <Row
                              key={i}
                              left={m.patient}
                              right={`${m.gapMinutes}m gap`}
                              sub={`${fmt(m.from)} → ${fmt(m.to)}`}
                              danger
                            />
                          ))}
                        </Section>
                      )}

                      {entry.checks.length === 0 && entry.liveViews.length === 0 && entry.logins.length === 0 && (
                        <p style={{ fontSize: "13px", color: "#94a3b8", margin: 0 }}>No activity in this period.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function Pill({ icon, label, color, bg }: { icon: React.ReactNode; label: string; color: string; bg: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: bg, color, fontSize: "11px", fontWeight: 600, padding: "3px 8px", borderRadius: "20px" }}>
      {icon}{label}
    </span>
  );
}

function Section({ title, color, children }: { title: string; color: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: "11px", fontWeight: 700, color, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "8px" }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>{children}</div>
    </div>
  );
}

function Row({ left, right, sub, danger }: { left: string; right?: string; sub?: string; danger?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", background: danger ? "#fef2f2" : "#f8fafc", borderRadius: "8px", padding: "8px 12px" }}>
      <div>
        <div style={{ fontSize: "13px", fontWeight: 600, color: danger ? "#dc2626" : "#0f172a" }}>{left}</div>
        {sub && <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>{sub}</div>}
      </div>
      {right && <span style={{ fontSize: "12px", color: danger ? "#dc2626" : "#64748b", whiteSpace: "nowrap", marginLeft: "12px" }}>{right}</span>}
    </div>
  );
}
